// src/hooks/useFilters.js
// ============================================================
// FILTER STATE HOOK - Dashboard filter management
// ============================================================
// Extracts filter-related state from useKegOpsState to reduce complexity.
// Manages: search, tap status, clean status, location, quick filter
// ============================================================

import { useState, useEffect } from 'react';

/**
 * useFilters - Manages all dashboard filter state
 * @returns {Object} Filter state and setters
 */
export const useFilters = () => {
  // Search
  const [searchTerm, setSearchTerm] = useState('');
  
  // Multi-select filters
  const [filterTapStatus, setFilterTapStatus] = useState([]);
  const [filterCleanStatus, setFilterCleanStatus] = useState([]);
  
  // Single-select filter
  const [filterLocation, setFilterLocation] = useState('all');
  
  // Dropdown visibility
  const [showTapDropdown, setShowTapDropdown] = useState(false);
  const [showCleanDropdown, setShowCleanDropdown] = useState(false);
  
  // Quick filter radio
  const [quickFilter, setQuickFilter] = useState('all');
  
  // Location expansion state (for grouped views)
  const [expandedLocations, setExpandedLocations] = useState({});

  // Auto-adjust tap status filter when quick filter changes
  useEffect(() => {
    if (quickFilter === 'empty') {
      setFilterTapStatus(['Empty']);
    } else if (quickFilter === 'filled') {
      setFilterTapStatus(['Conditioning', 'Lagering', 'Carbonating', 'On Deck', 'On Tap']);
    } else {
      setFilterTapStatus([]);
    }
  }, [quickFilter]);

  /**
   * Toggle a tap status filter value
   */
  const toggleTapStatusFilter = (status) => {
    setFilterTapStatus(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status) 
        : [...prev, status]
    );
  };

  /**
   * Toggle a clean status filter value
   */
  const toggleCleanStatusFilter = (status) => {
    setFilterCleanStatus(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status) 
        : [...prev, status]
    );
  };

  /**
   * Toggle location expansion in grouped views
   */
  const toggleLocationExpansion = (locationId) => {
    setExpandedLocations(prev => ({
      ...prev,
      [locationId]: !prev[locationId]
    }));
  };

  /**
   * Reset all filters to defaults
   */
  const resetFilters = () => {
    setSearchTerm('');
    setFilterTapStatus([]);
    setFilterCleanStatus([]);
    setFilterLocation('all');
    setQuickFilter('all');
  };

  return {
    // State
    searchTerm,
    filterTapStatus,
    filterCleanStatus,
    filterLocation,
    showTapDropdown,
    showCleanDropdown,
    quickFilter,
    expandedLocations,
    
    // Setters
    setSearchTerm,
    setFilterTapStatus,
    setFilterCleanStatus,
    setFilterLocation,
    setShowTapDropdown,
    setShowCleanDropdown,
    setQuickFilter,
    setExpandedLocations,
    
    // Helpers
    toggleTapStatusFilter,
    toggleCleanStatusFilter,
    toggleLocationExpansion,
    resetFilters
  };
};

export default useFilters;
